import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

/**
 * True only for a real file drag from the OS.
 *
 * Dragging selected text, a link or an image *inside* the app also fires the
 * same events, and without this check the overlay would pop up when someone
 * simply drags a word across a message.
 */
const dragHasFiles = (transfer: DataTransfer | null) =>
  Array.from(transfer?.types ?? []).includes('Files');

type Options = {
  onFiles: (files: File[]) => void;
  /** Drops are ignored and the overlay never shows while this is false. */
  enabled?: boolean;
};

/**
 * Drag-and-drop file staging for an element.
 *
 * Spread `dropzoneProps` on the element that should accept files and render
 * an overlay while `isDraggingOver`.
 */
export const useFileDropzone = ({ onFiles, enabled = true }: Options) => {
  const [isDraggingOver, setIsDraggingOver] = useState(false);

  /**
   * `dragenter` / `dragleave` fire per element, so moving the pointer from the
   * zone onto a bubble inside it emits leave-then-enter. A boolean would strobe
   * on every element the pointer crosses; counting depth keeps the overlay
   * steady until the drag genuinely leaves.
   */
  const depth = useRef(0);

  const onFilesRef = useRef(onFiles);
  useEffect(() => {
    onFilesRef.current = onFiles;
  }, [onFiles]);

  const reset = useCallback(() => {
    depth.current = 0;
    setIsDraggingOver(false);
  }, []);

  useEffect(() => {
    if (!enabled) {
      reset();
      return undefined;
    }

    /**
     * A file dropped anywhere *outside* the zone makes the browser navigate to
     * it - the whole SPA is torn down and the draft, the staged uploads and the
     * scroll position go with it. Cancelling the window-level default turns a
     * near-miss into a no-op instead. Only file drags are swallowed, so
     * in-app drags keep working.
     */
    const swallow = (event: DragEvent) => {
      if (dragHasFiles(event.dataTransfer)) event.preventDefault();
    };

    const onWindowDrop = (event: DragEvent) => {
      swallow(event);
      reset();
    };

    /*
     * Deliberately no window-level `dragleave` guard here. The obvious one -
     * treating `relatedTarget === null` as "the pointer left the window" - is
     * wrong in Chrome, which never populates `relatedTarget` on drag events at
     * all, so it fires on every element crossed inside the zone and takes the
     * overlay down mid-drag. Leaving the zone (or the window) already emits a
     * dragleave on the zone itself, which the counter below handles.
     */
    window.addEventListener('dragover', swallow);
    window.addEventListener('drop', onWindowDrop);

    return () => {
      window.removeEventListener('dragover', swallow);
      window.removeEventListener('drop', onWindowDrop);
      reset();
    };
  }, [enabled, reset]);

  const onDragEnter = useCallback(
    (event: React.DragEvent) => {
      if (!enabled || !dragHasFiles(event.dataTransfer)) return;
      event.preventDefault();
      depth.current += 1;
      setIsDraggingOver(true);
    },
    [enabled]
  );

  const onDragOver = useCallback(
    (event: React.DragEvent) => {
      if (!enabled || !dragHasFiles(event.dataTransfer)) return;
      // A drop only reaches an element that cancels `dragover`. Without this
      // the zone silently refuses every file and the cursor shows "no entry".
      event.preventDefault();
      event.dataTransfer.dropEffect = 'copy';
    },
    [enabled]
  );

  const onDragLeave = useCallback(
    (event: React.DragEvent) => {
      if (!enabled || !dragHasFiles(event.dataTransfer)) return;
      depth.current = Math.max(0, depth.current - 1);
      if (depth.current === 0) setIsDraggingOver(false);
    },
    [enabled]
  );

  const onDrop = useCallback(
    (event: React.DragEvent) => {
      if (!enabled || !dragHasFiles(event.dataTransfer)) return;
      event.preventDefault();
      reset();

      const files = Array.from(event.dataTransfer.files);
      if (files.length > 0) onFilesRef.current(files);
    },
    [enabled, reset]
  );

  const dropzoneProps = useMemo(
    () => ({ onDragEnter, onDragOver, onDragLeave, onDrop }),
    [onDragEnter, onDragOver, onDragLeave, onDrop]
  );

  return { isDraggingOver, dropzoneProps };
};
